// Global top navigation — sticky navy bar rendered above every
// authenticated page.
//
// April 2026 design refresh:
//   * Brand mark = stewardship logo image + tiny skewed yellow square
//     + "STEWARDSHIP OS" text. The skewed yellow dot is one of the
//     brand's signature moves.
//   * Active-route highlighting: nav link gets the yellow swoosh
//     underline (skewX(-8deg), 3px tall, sits 2px below baseline).
//   * Right cluster: 240px search button with ⌘K kbd hint, bell
//     (NotificationsDropdown), avatar (UserMenu).
//   * Static sticky navy — no scroll-to-glass overlay (dropped in the
//     v2 design, kept the GLASS_SCROLL_THRESHOLD export for tests
//     that still reference it).
//
// Accessibility:
//   * Semantic <nav> landmark with aria-label="Primary"
//   * Skip-to-main-content link above everything (hidden until
//     focused) — targets #main-content which PageLayout renders
//   * Focus-visible rings on every interactive element
//
// HELP-OK: 2026-08-27 change is an infinite-render crash fix
// (Sentry JAVASCRIPT-REACT-3N) — memoizing the item list so it stops
// churning identity every render. No label, button, or behavior a
// user would notice changed; nothing for the help database to update.

import { Menu, Search } from "lucide-react";
import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import type { SessionUser } from "../api";
import { useChrome } from "./ChromeContext";
import {
  detectMacLikePlatform,
  shortcutModifierLabel,
} from "../hooks/keyboard-shortcuts-helpers";
import { useStewNudges } from "../hooks/useStewNudges";
import { NotificationsDropdown } from "./NotificationsDropdown";
import { TopNavPrimaryNav } from "./TopNavPrimaryNav";
import {
  activeNavItemFor,
  displayRoleLabel,
  GLASS_SCROLL_THRESHOLD,
  initialsFor,
  NAV_ITEMS,
  visibleNavItems,
} from "./top-nav-helpers";
import { UserMenu } from "./UserMenu";

// Re-export the pure helpers so callers of TopNav get a single import
// point. The helpers live in top-nav-helpers.ts (no JSX) so the
// node-pool test suite can import them without the root tsconfig
// needing JSX support.
export {
  activeNavItemFor,
  displayRoleLabel,
  GLASS_SCROLL_THRESHOLD,
  initialsFor,
  NAV_ITEMS,
  visibleNavItems,
};
export type { NavItem } from "./top-nav-helpers";

interface TopNavProps {
  user: SessionUser;
  onSignOut: () => void;
}

export function TopNav({ user, onSignOut }: TopNavProps) {
  const location = useLocation();
  const chrome = useChrome();

  const activeItem = activeNavItemFor(location.pathname);
  // Memoized on the one field visibleNavItems() actually reads
  // (employee_role_slug / the ambassador check) rather than on `user`
  // itself, so a fresh session-object reference each render can't
  // still churn the array identity. TopNavPrimaryNav's overflow
  // measurement is keyed on this list re-running its effects on every
  // reference change (Sentry JAVASCRIPT-REACT-3N, 2026-08-27): an
  // unmemoized `.filter()` here handed it a brand-new array every
  // render, which reset its measured widths and re-showed every item,
  // which re-measured and hid the overflow again, forever.
  const navItems = useMemo(
    () => visibleNavItems(user),
    [user?.employee_role_slug],
  );

  return (
    <>
      <a className="skip-to-main" href="#main-content">
        Skip to main content
      </a>
      <header className="top-nav-shell" data-testid="top-nav">
        {/* Hamburger — mobile only (CSS hides it above 768px). Opens the
            sidebar as an off-canvas drawer. The sidebar is display:none
            at this width, so without this it would be unreachable. */}
        <button
          type="button"
          className="top-nav-icon-btn top-nav-menu-btn"
          aria-label="Open navigation menu"
          aria-expanded={chrome.isMobileNavOpen}
          onClick={chrome.openMobileNav}
          data-testid="top-nav-menu-btn"
        >
          <Menu size={18} strokeWidth={1.75} aria-hidden="true" />
        </button>
        <Link
          to="/dashboard"
          className="top-nav-brand"
          aria-label="Stewardship OS home"
        >
          <img
            src="/stewardship-logo.png"
            alt=""
            className="top-nav-logo"
            aria-hidden="true"
          />
          <span>OS</span>
        </Link>
        {/* Overflow-aware: items that don't fit move into a "More" menu
            instead of being sliced mid-word behind an invisible scroll
            (Grant, 2026-07-25). See TopNavPrimaryNav. */}
        {/* A plain Home link ahead of the record lanes (2026-10-15). The
            logo already went to /dashboard, but nothing said so; Grok's
            UX walk and Grant's own audit both found people with no
            visible way back to where they land. Hidden at phone width
            with the lanes (the drawer's Home section covers it). */}
        <div className="top-nav-home">
          <Link
            to="/dashboard"
            className={`top-nav-link${location.pathname === "/dashboard" ? " is-active" : ""}`}
            aria-current={location.pathname === "/dashboard" ? "page" : undefined}
            data-testid="top-nav-home-link"
          >
            Home
          </Link>
          <span className="top-nav-home-sep" aria-hidden="true" />
        </div>
        <TopNavPrimaryNav items={navItems} activeTo={activeItem?.to ?? null} />
        <div className="top-nav-cluster">
          {/* Ask Stew button (rebound from the old GlobalSearch
              button as of Ask Stew V1 PR #3). Same visual treatment
              + ⌘K/Ctrl+K shortcut; since S2 PR 3 the panel is the
              search-first palette. */}
          <AskStewTopNavButton onClick={chrome.openAskStew} />
          {/* S2 PR 4 (audit 9.4) — phones hide the full button, so an
              icon-only search affordance shows at small widths (CSS
              swaps them; ⌘K doesn't exist on touch). */}
          <button
            type="button"
            className="top-nav-icon-btn top-nav-search-icon-btn"
            aria-label="Search and Ask Stew"
            onClick={chrome.openAskStew}
            data-testid="top-nav-search-icon-btn"
          >
            <Search size={18} strokeWidth={1.75} aria-hidden="true" />
          </button>
          {/* The settings gear was removed 2026-10-15 (navigation
              overhaul): it opened the same page as avatar menu → My
              settings, two doors to one room in the same corner. */}
          <NotificationsDropdown />
          <UserMenu user={user} onSignOut={onSignOut} />
        </div>
      </header>
    </>
  );
}

// V1.5.4 — Ask Stew top-nav button + nudge badge. Pulled out as a
// sub-component so the useStewNudges hook doesn't make the whole
// TopNav re-render on every nudge poll (only this button does).
function AskStewTopNavButton({ onClick }: { onClick: () => void }) {
  const { count } = useStewNudges();
  // S2 PR 4 (audit 8.5) — the handler always accepted Ctrl+K; now the
  // LABEL matches the user's platform too ("Ctrl K" on Windows).
  const modifier = useMemo(
    () => shortcutModifierLabel(detectMacLikePlatform()),
    [],
  );
  const shortcutHint = `${modifier}K`;
  return (
    <button
      type="button"
      className="top-nav-search-btn"
      aria-label={
        count > 0
          ? `Open Ask Stew (${count} new nudge${count === 1 ? "" : "s"})`
          : "Open Ask Stew"
      }
      title={
        count > 0
          ? `Ask Stew (${shortcutHint}) — ${count} new nudge${count === 1 ? "" : "s"}`
          : `Ask Stew (${shortcutHint})`
      }
      onClick={onClick}
      data-testid="top-nav-ask-stew-btn"
    >
      <Search size={14} strokeWidth={1.75} aria-hidden="true" />
      <span className="top-nav-search-label">Ask Stew</span>
      {count > 0 && (
        <span
          className="top-nav-stew-badge"
          data-testid="top-nav-stew-badge"
          aria-hidden="true"
        >
          {count > 9 ? "9+" : count}
        </span>
      )}
      <kbd>{shortcutHint}</kbd>
    </button>
  );
}
